import { tracking } from '@iwoca/frontend-tracking-library';
import { stringify } from 'qs';
import { useNavigate } from 'react-router';

import { getQueryParams } from '../../helpers/getQueryParams';
import { usePutStateMutation } from '../../lendingApi/lendingApiMutations';
import { useGetStateQuery } from '../../lendingApi/lendingApiQueries';
import {
  TGetStateResponse,
  TPutStateRequestBody,
  TSignupStepsUK,
} from '../../types';
import { ACCOUNT_PAGES_URL } from '../../urls';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';
import logger from '../../utils/logger';
import { useContextStateKey } from '../useContextStateKey';
import { useCurrentStep } from '../useCurrentStep';
import { useSignupParams } from '../useSignupParams';
import { useImpersonatedStateKey } from '../useStateKey';

export const useSignupRouterUK = () => {
  const { rawAffiliateParam } = useSignupParams();
  const navigate = useNavigate();

  const { state } = useGetStateQuery<TGetStateResponse>();

  const { mutatePutState } = usePutStateMutation();

  const basePath = `/${rawAffiliateParam}`;

  const currentStep = useCurrentStep();

  const stateKey = useContextStateKey();
  const impersonatedStateKey = useImpersonatedStateKey();

  const navigateSignup = async ({
    route,
    updatedState,
    beforeNavigate,
  }: {
    route: TSignupStepsUK;
    updatedState?: TPutStateRequestBody['data'];
    beforeNavigate?: () => void | Promise<void>;
  }) => {
    if (!stateKey) {
      return logger.error(
        'Attempted to navigate without stateKey. StateKey is required for the PUT to state.',
      );
    }

    let newState = updatedState || state;

    if (route === 'decision') {
      tracking.ampli.signupCompleted(getAmplitudeDefaultEventProperties());

      newState = {
        ...newState,
        ui: {
          ...newState.ui,
          activeStep: {
            id: 'myAccount',
          },
        },
      };
    }

    if (route !== 'new' && newState.ui?.activePartnerStep !== route) {
      const putStateBody: TPutStateRequestBody = {
        data: {
          ...newState,
          ui: {
            ...newState.ui,
            activePartnerStep: route,
          },
        },
      };

      await mutatePutState({
        stateKey,
        body: putStateBody,
      });
    }

    beforeNavigate?.();

    const newQueryParams = {
      ...getQueryParams(),
    };

    if (impersonatedStateKey) {
      newQueryParams.state_key = impersonatedStateKey;
    }

    let newPath = `${route}/`;
    if (stringify(newQueryParams)) {
      newPath = `${route}/?${stringify(newQueryParams)}`;
    }

    navigate(`${basePath}/${newPath}`);
  };

  const navigateSignupWithoutStateUpdate = (newRoute: TSignupStepsUK) => {
    const newQueryParams = {
      ...getQueryParams(),
    };

    if (impersonatedStateKey) {
      newQueryParams.state_key = impersonatedStateKey;
    }

    let newPath = `${newRoute}/`;
    if (stringify(newQueryParams)) {
      newPath = `${newRoute}/?${stringify(newQueryParams)}`;
    }

    navigate(`${basePath}/${newPath}`);
  };

  const navigateToAccountPages = async (
    stateKey: string,
    updatedState?: TPutStateRequestBody['data'],
  ) => {
    const newState = updatedState || state;

    const putStateBody: TPutStateRequestBody = {
      data: {
        ...newState,
        ui: {
          ...newState.ui,
          activeStep: {
            id: 'myAccount',
          },
        },
      },
    };

    await mutatePutState({
      stateKey,
      body: putStateBody,
    });

    tracking.ampli.signupCompleted(getAmplitudeDefaultEventProperties());

    window.location.href = ACCOUNT_PAGES_URL;
  };

  return {
    basePath,
    currentStep,
    navigateSignup,
    navigateSignupWithoutStateUpdate,
    navigateToAccountPages,
  };
};
